import { useCallback, useLayoutEffect, useRef } from 'react';
import type React from 'react';

const OVERLAY_FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'details > summary:first-of-type',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const isFocusableElement = (element: HTMLElement | null): boolean => {
  if (!element?.isConnected || element.tabIndex < 0) return false;
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
  if ('disabled' in element && Boolean((element as HTMLButtonElement).disabled)) return false;
  return true;
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(OVERLAY_FOCUSABLE_SELECTOR)).filter(
    isFocusableElement
  );

const focusElement = (element: HTMLElement, ensureVisible = false) => {
  try {
    element.focus({ preventScroll: true });
  } catch (_error) {
    element.focus();
  }

  if (ensureVisible) {
    try {
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch (_error) {
      // Older browsers and the test DOM can omit scrollIntoView.
    }
  }
};

const restoreOverlayFocus = (target: HTMLElement | null) => {
  if (!target) return;
  if (isFocusableElement(target)) {
    focusElement(target);
    return;
  }

  if (!target.isConnected) return;
  let scope = target.parentElement;
  while (scope && scope !== document.body) {
    const fallback = getFocusableElements(scope).find((element) => element !== target);
    if (fallback) {
      focusElement(fallback);
      return;
    }
    scope = scope.parentElement;
  }
};

let modalBodyLockCount = 0;
let modalBodyPreviousOverflow = '';
let modalBodyPreviousOverscrollBehavior = '';

const lockModalBodyScroll = () => {
  if (modalBodyLockCount === 0) {
    modalBodyPreviousOverflow = document.body.style.overflow;
    modalBodyPreviousOverscrollBehavior = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
  }
  modalBodyLockCount += 1;

  return () => {
    modalBodyLockCount = Math.max(0, modalBodyLockCount - 1);
    if (modalBodyLockCount === 0) {
      document.body.style.overflow = modalBodyPreviousOverflow;
      document.body.style.overscrollBehavior = modalBodyPreviousOverscrollBehavior;
      modalBodyPreviousOverflow = '';
      modalBodyPreviousOverscrollBehavior = '';
    }
  };
};

type ModalFocusLifecycleOptions = {
  isOpen: boolean;
  dialogRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  initialFocusRef?: React.RefObject<HTMLElement>;
  returnFocusRef?: React.RefObject<HTMLElement>;
};

export const useModalFocusLifecycle = ({
  isOpen,
  dialogRef,
  onClose,
  initialFocusRef,
  returnFocusRef,
}: ModalFocusLifecycleOptions) => {
  const onCloseRef = useRef(onClose);
  const initialFocusRefRef = useRef(initialFocusRef);
  const returnFocusRefRef = useRef(returnFocusRef);

  onCloseRef.current = onClose;
  initialFocusRefRef.current = initialFocusRef;
  returnFocusRefRef.current = returnFocusRef;

  useLayoutEffect(() => {
    if (!isOpen) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const activeElement =
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null;
    const restoreTarget = returnFocusRefRef.current?.current || activeElement;
    const unlockBodyScroll = lockModalBodyScroll();

    const focusInside = () => {
      const explicitTarget = initialFocusRefRef.current?.current || null;
      const initialTarget =
        explicitTarget && dialog.contains(explicitTarget) && isFocusableElement(explicitTarget)
          ? explicitTarget
          : getFocusableElements(dialog)[0] || dialog;
      focusElement(initialTarget, true);
    };

    focusInside();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (event.repeat) return;
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        focusElement(dialog, true);
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const activeIndex = active ? focusableElements.indexOf(active) : -1;

      if (
        activeIndex === -1 ||
        (event.shiftKey && active === first) ||
        (!event.shiftKey && active === last)
      ) {
        event.preventDefault();
        focusElement(event.shiftKey ? last : first, true);
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.contains(event.target)) {
        event.stopPropagation();
        focusInside();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    const observer =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            const currentFocus = document.activeElement;
            if (!(currentFocus instanceof Node) || !dialog.contains(currentFocus)) {
              focusInside();
            }
          });
    observer?.observe(dialog, { attributes: true, childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      unlockBodyScroll();
      restoreOverlayFocus(restoreTarget);
    };
  }, [dialogRef, isOpen]);
};

type PopoverFocusLifecycleOptions = {
  isOpen: boolean;
  panelRef: React.RefObject<HTMLElement>;
  triggerRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  focusOnOpen: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
};

type ClosePopoverOptions = {
  returnFocus?: boolean;
};

export const usePopoverFocusLifecycle = ({
  isOpen,
  panelRef,
  triggerRef,
  onClose,
  focusOnOpen,
  initialFocusRef,
}: PopoverFocusLifecycleOptions) => {
  const onCloseRef = useRef(onClose);
  const shouldReturnFocusRef = useRef(false);
  const hadFocusInsideRef = useRef(false);
  const lastFocusedInsideRef = useRef<HTMLElement | null>(null);

  onCloseRef.current = onClose;

  const closePopover = useCallback(({ returnFocus = false }: ClosePopoverOptions = {}) => {
    shouldReturnFocusRef.current = returnFocus;
    onCloseRef.current();
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const panel = panelRef.current;
    if (!panel) return;
    const trigger = triggerRef.current;

    shouldReturnFocusRef.current = false;
    hadFocusInsideRef.current = false;
    lastFocusedInsideRef.current = null;

    const focusInside = () => {
      const explicitTarget = initialFocusRef?.current || null;
      const initialTarget =
        explicitTarget && panel.contains(explicitTarget) && isFocusableElement(explicitTarget)
          ? explicitTarget
          : getFocusableElements(panel)[0] || panel;
      focusElement(initialTarget, true);
      hadFocusInsideRef.current = true;
      lastFocusedInsideRef.current = initialTarget;
    };

    if (focusOnOpen) {
      focusInside();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.repeat) return;
      event.preventDefault();
      event.stopPropagation();
      shouldReturnFocusRef.current = true;
      restoreOverlayFocus(trigger);
      onCloseRef.current();
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Node && panel.contains(event.target)) {
        hadFocusInsideRef.current = true;
        lastFocusedInsideRef.current = event.target instanceof HTMLElement ? event.target : null;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    const observer =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            if (!hadFocusInsideRef.current) return;
            const lastFocusedInside = lastFocusedInsideRef.current;
            if (lastFocusedInside?.isConnected && panel.contains(lastFocusedInside)) return;
            const currentFocus = document.activeElement;
            if (!(currentFocus instanceof Node) || !panel.contains(currentFocus)) {
              focusInside();
            }
          });
    observer?.observe(panel, { attributes: true, childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      if (shouldReturnFocusRef.current) {
        restoreOverlayFocus(trigger);
      }
    };
  }, [focusOnOpen, initialFocusRef, isOpen, panelRef, triggerRef]);

  return { closePopover };
};
